import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronDownIcon, 
  ChevronRightIcon,
  BuildingOfficeIcon,
  GlobeAsiaAustraliaIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  UserIcon,
  StarIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { getUsersData, searchUsers, buildTree } from '../data/users';
import UserDeepViewModal from '../components/UserDeepViewModal';
import { notificationService } from '../services/notificationService';
import { useNotification, NOTIFICATION_TYPES } from '../components/NotificationContext';
import { useRefresh } from '../components/RefreshContext';
import { 
  subscribeToBalanceUpdates,
  subscribeToUserUpdates
} from '../services/userTreeService';
import useTranslation from '../services/translationService';
import { amountFractionDigits } from '../utils/currency';

const USER_TYPES = {
  ADMIN: 'admin',
  COUNTRY_ADMIN: 'country',
  CITY_ADMIN: 'city',
  SUPER_AGENT: 'super-agent',
  AGENT: 'agent',
  PLAYER: 'player'
};

// Currency sort order
const CURRENCY_ORDER = ['LBP', 'USD', 'TND', 'TRY', 'IQD', 'KWD'];

const sortCurrencies = (entries) => {
  return [...entries].sort(([currencyA], [currencyB]) => {
    const indexA = CURRENCY_ORDER.indexOf(currencyA);
    const indexB = CURRENCY_ORDER.indexOf(currencyB);
    
    // If both are in the order array, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only A is in the array, A comes first
    if (indexA !== -1) return -1;
    // If only B is in the array, B comes first
    if (indexB !== -1) return 1;
    // If neither is in the array, sort alphabetically
    return currencyA.localeCompare(currencyB);
  });
};

// Add hierarchy levels for each user type
const USER_HIERARCHY_LEVELS = {
  [USER_TYPES.ADMIN]: 5,
  [USER_TYPES.COUNTRY_ADMIN]: 4,
  [USER_TYPES.CITY_ADMIN]: 3,
  [USER_TYPES.SUPER_AGENT]: 2,
  [USER_TYPES.AGENT]: 1,
  [USER_TYPES.PLAYER]: 0
};

// Lightweight error boundary for individual tree nodes
class TreeNodeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('TreeNode render error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-2 text-xs text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-800">
          Failed to render node
        </div>
      );
    }
    return this.props.children;
  }
}

const Legend = ({ onTypeSelect, selectedType, userType }) => {
  const { t } = useTranslation();
  const userLevel = USER_HIERARCHY_LEVELS[userType] || 0;

  const items = [
    { 
      type: USER_TYPES.ADMIN, 
      label: t('userTypes.admin', 'Admin'), 
      icon: BuildingOfficeIcon, 
      color: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20', 
      level: USER_HIERARCHY_LEVELS[USER_TYPES.ADMIN] 
    },
    { 
      type: USER_TYPES.COUNTRY_ADMIN, 
      label: t('userTypes.country', 'Country'), 
      icon: GlobeAsiaAustraliaIcon, 
      color: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20', 
      level: USER_HIERARCHY_LEVELS[USER_TYPES.COUNTRY_ADMIN] 
    },
    { 
      type: USER_TYPES.CITY_ADMIN, 
      label: t('userTypes.city', 'City'), 
      icon: BuildingStorefrontIcon, 
      color: 'text-green-500 bg-green-50 dark:bg-green-900/20', 
      level: USER_HIERARCHY_LEVELS[USER_TYPES.CITY_ADMIN] 
    },
    { 
      type: USER_TYPES.SUPER_AGENT, 
      label: t('userTypes.super-agent', 'Super Agent'), 
      icon: StarIcon, 
      color: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20', 
      level: USER_HIERARCHY_LEVELS[USER_TYPES.SUPER_AGENT] 
    },
    { 
      type: USER_TYPES.AGENT, 
      label: t('userTypes.agent', 'Agent'), 
      icon: UserGroupIcon, 
      color: 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20', 
      level: USER_HIERARCHY_LEVELS[USER_TYPES.AGENT] 
    },
    { 
      type: USER_TYPES.PLAYER, 
      label: t('userTypes.player', 'Player'), 
      icon: UserIcon, 
      color: 'text-gray-500 bg-gray-50 dark:bg-gray-800', 
      level: USER_HIERARCHY_LEVELS[USER_TYPES.PLAYER] 
    },
  ];

  // Filter items based on user's hierarchy level
  const visibleItems = items.filter(item => item.level <= userLevel);

  return (
    <div className="legend-container">
      {visibleItems.length > 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
          {t('agentTree.legendHelp', 'Click on any type to filter the tree view')}
        </p>
      )}
      <div className="legend-grid">
        {visibleItems.map(({ type, label, icon: Icon, color }) => (
          <button
            key={type}
            onClick={() => onTypeSelect(type)}
            className={`legend-item cursor-pointer transition-all duration-200
              ${selectedType === type ? 'scale-105 shadow-md' : 'hover:scale-105'}`}
          >
            <div className={`legend-icon-wrapper ${color} ${selectedType === type ? 'ring-2 ring-offset-2 ring-current' : ''}`}>
              <Icon className="w-4 h-4" />
            </div>
            <span className={`legend-label ${selectedType === type ? 'font-medium' : ''}`}>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const SearchBar = ({ onSearch, searchTerm, onClear }) => {
  const { t } = useTranslation();
  
  return (
    <div className="search-bar-container">
      <div className="search-bar-wrapper">
        <input
          type="text"
          placeholder={t('agentTree.searchPlaceholder', 'Search agents (min. 3 characters)...')}
          onChange={(e) => onSearch(e.target.value)}
          value={searchTerm}
          className="search-input"
        />
        {searchTerm ? (
          <button
            onClick={onClear}
            className="search-clear-button"
            title={t('common.clear', 'Clear search')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        ) : (
          <MagnifyingGlassIcon className="search-icon-right" />
        )}
      </div>
    </div>
  );
};

const getDirectReportsCount = (node) => {
  if (!node.children || node.type === USER_TYPES.PLAYER) return 0;
  return node.children.length;
};

const CurrencyBadges = ({ currencies }) => {
  if (!currencies || currencies.length === 0) return null;

  return (
    <div className={`flex flex-wrap gap-2 mt-1 ${currencies.length === 1 ? 'w-fit' : 'w-full'}`}>
      {currencies.map((currency, index) => (
        <div
          key={currency}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
        >
          <span>{currency}</span>
        </div>
      ))}
    </div>
  );
};

const TreeNode = React.memo(({ node, searchTerm, isSearchResult, path = '', selectedType, rootUserType, parentNode = null, onOpenDeepView }) => {
  // Move all hooks to the top
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  // Add useEffect hook at the top
  useEffect(() => {
    if (!searchTerm) {
      setIsExpanded(false);
    }
  }, [searchTerm]);

  // Add useCallback hook at the top
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  }, []);

  // Add both useMemo hooks at the top
  const style = useMemo(() => 
    node && node.type ? getNodeStyle(node.type) : getNodeStyle('player'), 
    [node?.type]
  );
  
  const highlightedName = useMemo(() => {
    if (!node || !searchTerm || !node.name || searchTerm.length < 3) {
      return node?.name || 'Unknown';
    }
    
    try {
      const regex = new RegExp(`(${searchTerm})`, 'gi');
      const parts = node.name.split(regex);
      return (
        <span>
          {parts.map((part, i) => 
            part.toLowerCase() === searchTerm.toLowerCase() ? (
              <span key={i} className="highlight-match">{part}</span>
            ) : part
          )}
        </span>
      );
    } catch (error) {
      return node.name || 'Unknown';
    }
  }, [node?.name, searchTerm]);

  // Safety check for invalid node - after all hooks are called
  if (!node || typeof node !== 'object') {
    console.warn('Invalid node passed to TreeNode:', node);
    return null;
  }

  // Get currency style based on type
  const getCurrencyStyle = (currency) => {
    switch (currency) {
      case 'USD':
        return 'text-emerald-600 dark:text-emerald-400';
      case 'LBP':
        return 'text-blue-600 dark:text-blue-400';
      case 'TND':
        return 'text-purple-600 dark:text-purple-400';
      case 'TRY':
        return 'text-red-600 dark:text-red-400';
      case 'IQD':
        return 'text-indigo-600 dark:text-indigo-400';
      case 'KWD':
        return 'text-amber-600 dark:text-amber-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Format balance with currency (default to 0 if not present)
  const formatBalance = (amount = 0, currency = 'USD') => {
    const digits = amountFractionDigits(currency, amount);
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount);
  };


  // Update the search filtering condition - moved after all hooks
  if (searchTerm && searchTerm.length >= 3 && !isSearchResult) {
    const nodeName = node.name || '';
    const searchTermLower = searchTerm.toLowerCase();
    const nodeNameLower = nodeName.toLowerCase();
    
    if (!nodeNameLower.includes(searchTermLower)) {
      return null;
    }
  }

  // Ensure we have a valid username
  const username = node.username || 'unknown';
  const hasChildren = node.children && Array.isArray(node.children) && node.children.length > 0;
  const Icon = style.icon;

  return (
    <div className="tree-node">
      <div 
        className={`tree-node-content group ${style.bg} ${
          searchTerm && node.name && node.name.toLowerCase().includes(searchTerm.toLowerCase())
            ? 'search-match'
            : ''
        }`}
      >
        <div className="flex items-center flex-1 w-full">
          <div className="flex items-center w-full">
            {hasChildren && (
              <div className="relative">
                <button 
                  className="tree-node-toggle"
                  onClick={handleClick}
                >
                  {isExpanded ? 
                    <ChevronDownIcon className="w-4 h-4" /> : 
                    <ChevronRightIcon className="w-4 h-4" />
                  }
                </button>
                {getDirectReportsCount(node) > 0 && (
                  <span className="absolute -top-2 -left-2 flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] font-medium">
                    {getDirectReportsCount(node)}
                  </span>
                )}
              </div>
            )}
            <div 
              className="tree-node-content-inner w-full"
              onClick={handleClick}
            >
              <div className={`tree-node-icon ${style.color} flex items-center justify-center -mr-1`}>
                <Icon className="w-7 h-7" />
              </div>
              <div className="tree-node-details ml-1">
                <div className="tree-node-info-lines">
                  <div className="tree-node-name">
                    {highlightedName}
                  </div>
                  {username && (
                    <div className="flex items-center gap-2 mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/users/${username}/deep-view`);
                        }}
                        className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700/50 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600/50 hover:border-gray-300 dark:hover:border-gray-500 transition-all duration-150 cursor-pointer active:scale-95"
                        title={`View ${username} details`}
                      >
                        @{username}
                      </button>
                      {parentNode && (isSearchResult || selectedType) && (
                        <>
                          <span className="text-xs text-gray-300 dark:text-gray-600">•</span>
                          <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[140px] font-medium" title={`Parent: ${parentNode.name || parentNode.username}`}>
                            ↑ {parentNode.name || parentNode.username}
                          </span>
                        </>
                      )}
                    </div>
                  )}
                  <div className="relative mt-1.5">
                    {node.balances && Object.keys(node.balances).length > 0 && (
                      <div className="absolute -top-2 -left-2 flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] font-medium">
                        {Object.keys(node.balances).length}
                      </div>
                    )}
                    <div className="tree-node-financial flex items-center text-sm w-full overflow-x-auto scrollbar-hide">
                      {node.balances && Object.keys(node.balances).length > 0 ? (
                        <div className="flex items-center bg-white/50 dark:bg-gray-800/50 rounded-lg px-2 py-1 pr-3 border border-gray-100 dark:border-gray-700 shadow-sm">
                          {sortCurrencies(Object.entries(node.balances)).map(([currency, balance], index) => (
                            <div 
                              key={currency}
                              className="flex items-center whitespace-nowrap px-2 first:pl-0 last:pr-0 shrink-0 border-r border-gray-200 dark:border-gray-600 last:border-r-0"
                            >
                              <span className={`font-semibold ${getCurrencyStyle(currency)}`}>
                                {formatBalance(balance, currency)}
                              </span>
                              <span className="ml-1 text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {currency}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="tree-node-actions">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onOpenDeepView) onOpenDeepView(node, parentNode);
            }}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-95 transition-all duration-150 shadow-sm"
            title="More actions"
          >
            <EllipsisVerticalIcon className="w-5 h-5 text-gray-700 dark:text-gray-200 stroke-[2.5]" />
          </button>
        </div>
      </div>

      {isExpanded && hasChildren && (
        <div className="tree-node-children">
          {[...node.children].reverse().map((child) => (
            <TreeNode 
              key={child.username || Math.random().toString(36).substring(7)}
              node={child}
              searchTerm={searchTerm}
              isSearchResult={isSearchResult}
              path={`${path}/${child.username || 'unknown'}`}
              selectedType={selectedType}
              rootUserType={rootUserType}
              parentNode={node}
              onOpenDeepView={onOpenDeepView}
            />
          ))}
        </div>
      )}
    </div>
  );
});

TreeNode.displayName = 'TreeNode';

const getNodeStyle = (type) => {
  switch (type) {
    case USER_TYPES.ADMIN:
      return {
        icon: BuildingOfficeIcon,
        color: 'text-purple-500',
        bg: 'bg-purple-50 dark:bg-purple-900/20'
      };
    case USER_TYPES.COUNTRY_ADMIN:
      return {
        icon: GlobeAsiaAustraliaIcon,
        color: 'text-blue-500',
        bg: 'bg-blue-50 dark:bg-blue-900/20'
      };
    case USER_TYPES.CITY_ADMIN:
      return {
        icon: BuildingStorefrontIcon,
        color: 'text-green-500',
        bg: 'bg-green-50 dark:bg-green-900/20'
      };
    case USER_TYPES.SUPER_AGENT:
      return {
        icon: StarIcon,
        color: 'text-orange-500',
        bg: 'bg-orange-50 dark:bg-orange-900/20'
      };
    case USER_TYPES.AGENT:
      return {
        icon: UserGroupIcon,
        color: 'text-yellow-500',
        bg: 'bg-yellow-50 dark:bg-yellow-900/20'
      };
    case USER_TYPES.PLAYER:
      return {
        icon: UserIcon,
        color: 'text-gray-500',
        bg: 'bg-gray-50 dark:bg-gray-800'
      };
    default:
      return {
        icon: UserIcon,
        color: 'text-gray-400',
        bg: 'bg-gray-50 dark:bg-gray-800'
      };
  }
};

const AgentTree = () => {
  const [treeData, setTreeData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [userType, setUserType] = useState(null);
  const [dataTimestamp, setDataTimestamp] = useState(0); // To track data freshness
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Lifted modal state (prevents modal from being destroyed when TreeNode unmounts)
  const [deepViewModalOpen, setDeepViewModalOpen] = useState(false);
  const [deepViewNode, setDeepViewNode] = useState(null);
  const [deepViewParent, setDeepViewParent] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const { registerRefreshFunction, unregisterRefreshFunction } = useRefresh();

  // Handlers for the lifted deep view modal
  const handleOpenDeepView = useCallback((node, parentNode) => {
    setDeepViewNode(node);
    setDeepViewParent(parentNode);
    setDeepViewModalOpen(true);
  }, []);

  const handleCloseDeepView = useCallback(() => {
    setDeepViewModalOpen(false);
    setDeepViewNode(null);
    setDeepViewParent(null);
  }, []);

  const handleNavigateToUser = useCallback(async (targetNode) => {
    if (!targetNode?.username) {
      console.error('handleNavigateToUser: Invalid target node', targetNode);
      return;
    }
    try {
      const { success, data } = await getUsersData();
      if (!success || !data?.users) {
        console.error('handleNavigateToUser: Failed to get users data');
        return;
      }
      const fullTargetNode = data.users[targetNode.username];
      if (!fullTargetNode) {
        console.error('handleNavigateToUser: Target user not found', targetNode.username);
        return;
      }
      const targetParentUsername = fullTargetNode.parentUsername;
      const targetParentNode = targetParentUsername ? data.users[targetParentUsername] : null;
      setDeepViewNode(fullTargetNode);
      setDeepViewParent(targetParentNode);
    } catch (error) {
      console.error('Error navigating to user:', error);
    }
  }, []);



  // Register for real-time notifications
  useEffect(() => {
    // Make sure notification service is initialized
    notificationService.initialize().catch(console.error);
    
    // Register notification handler that will refresh tree data when needed
    const cleanup = notificationService.onNotification((message) => {
      // If notification is transaction-related, refresh the tree data
      if (message.content && (
          message.content.includes('Transaction:') || 
          message.content.includes('deposit') || 
          message.content.includes('withdraw') ||
          message.content.includes('balance'))) {
        fetchData(); // Force refresh on transaction notification
      }
    });


    // Cleanup handler when component unmounts
    return cleanup;
  }, [showNotification]); // Include showNotification in dependencies

  // Register refresh function for external triggers
  useEffect(() => {
    registerRefreshFunction('agentTree', handleRefresh);
    
    return () => {
      unregisterRefreshFunction('agentTree');
    };
  }, [registerRefreshFunction, unregisterRefreshFunction]);

  // Debounce search term to prevent rapid re-renders on mobile
  useEffect(() => {
    // Clear immediately when search is cleared
    if (!searchTerm) {
      setDebouncedSearchTerm('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Parse the filter from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterType = params.get('filter');
    if (filterType && Object.values(USER_TYPES).includes(filterType)) {
      setSelectedType(filterType);
    }
  }, [location.search]);

  // Replace the old subscription effect with new ones
  useEffect(() => {
    // Subscribe to balance updates
    const balanceCleanup = subscribeToBalanceUpdates((username, newBalances) => {
      setTreeData(prevTree => {
        if (!prevTree) return prevTree;
        
        const updateNodeBalances = (node) => {
          if (node.username === username) {
            return {
              ...node,
              balances: newBalances
            };
          }
          if (node.children) {
            return {
              ...node,
              children: node.children.map(updateNodeBalances)
            };
          }
          return node;
        };
        
        return updateNodeBalances(prevTree);
      });
    });

    // Subscribe to user updates
    const userCleanup = subscribeToUserUpdates((username, userData) => {
      setTreeData(prevTree => {
        if (!prevTree) return prevTree;
        
        const updateNodeData = (node) => {
          if (node.username === username) {
            return {
              ...node,
              ...userData,
              children: node.children // Preserve children
            };
          }
          if (node.children) {
            return {
              ...node,
              children: node.children.map(updateNodeData)
            };
          }
          return node;
        };
        
        return updateNodeData(prevTree);
      });
    });

    // Return combined cleanup
    return () => {
      balanceCleanup();
      userCleanup();
    };
  }, []);

  // Define fetchData in component scope so it can be used by multiple effects
  const fetchData = async (forceRefresh = false) => {
    // Only set loading state if we don't have data yet
    if (!treeData) {
      setIsLoading(true);
    }
    
    setError(null);
    try {
      const response = await getUsersData(forceRefresh);
      if (response.success) {
        const tree = await buildTree(response.data, response.data.rootId);
        setTreeData(tree);
        // Set the user type from the root node (logged-in user)
        setUserType(tree.type);
        setDataTimestamp(Date.now());
      }
    } catch (error) {
      console.error('Error fetching tree data:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch tree data on mount - but only once
  useEffect(() => {
    if (!treeData) {
      fetchData(false); // Use cached data if available
    }
  }, [treeData]);

  // Make the fetchData function available globally for refreshing after user creation
  useEffect(() => {
    window.refreshUserTreeData = () => {
      // Only force refresh based on cache duration (10s for agents/super-agents, 5 min for others)
      const now = Date.now();
      const cacheDuration = (userType === 'super-agent' || userType === 'agent') ? 10 * 1000 : 5 * 60 * 1000;
      const shouldForceRefresh = !dataTimestamp || (now - dataTimestamp) > cacheDuration;
      fetchData(shouldForceRefresh);
    };

    // Cleanup
    return () => {
      delete window.refreshUserTreeData;
    };
  }, [dataTimestamp, userType]);

  // Update search results when debounced term changes
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
        // If we already have the tree data, search locally instead of making a new API call
        if (treeData) {
          const findMatchingNodes = (node, term) => {
            let results = [];
            const termLower = term.toLowerCase();
            
            // Check if current node matches
            if (node.name && node.name.toLowerCase().includes(termLower) ||
                node.username && node.username.toLowerCase().includes(termLower)) {
              results.push(node);
            }
            
            // Check children
            if (node.children) {
              node.children.forEach(child => {
                results = results.concat(findMatchingNodes(child, term));
              });
            }
            
            return results;
          };
          
          const results = findMatchingNodes(treeData, debouncedSearchTerm);
          // Sort by username length (shortest first)
          results.sort((a, b) => {
            const usernameA = a.username || '';
            const usernameB = b.username || '';
            return usernameA.length - usernameB.length;
          });
          setSearchResults(results);
        } else {
          // Fallback to API search if we don't have local data
          const results = await searchUsers(debouncedSearchTerm);
          // Sort by username length (shortest first)
          results.sort((a, b) => {
            const usernameA = a.username || '';
            const usernameB = b.username || '';
            return usernameA.length - usernameB.length;
          });
          setSearchResults(results);
        }
      } else {
        setSearchResults([]);
      }
    };

    performSearch();
  }, [debouncedSearchTerm, treeData, dataTimestamp]);

  // Define TYPE_LABELS mapping for better message content
  const TYPE_LABELS = {
    [USER_TYPES.ADMIN]: 'Admin',
    [USER_TYPES.COUNTRY_ADMIN]: 'Country',
    [USER_TYPES.CITY_ADMIN]: 'City',
    [USER_TYPES.SUPER_AGENT]: 'Super Agent',
    [USER_TYPES.AGENT]: 'Agent',
    [USER_TYPES.PLAYER]: 'Player'
  };

  const handleTypeFilter = async (type) => {
    setSelectedType(type);
  };

  // Find all nodes of a specific type
  const findNodesOfType = (node, type) => {
    if (!node) return [];
    
    let nodes = [];
    
    try {
      // Check if current node matches the type
      if (node.type === type) {
        nodes.push(node);
      }
      
      // Recursively process children
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(child => {
          if (child) {
            const childResults = findNodesOfType(child, type);
            nodes = nodes.concat(childResults);
          }
        });
      }
    } catch (error) {
      console.error(`Error finding nodes of type ${type}:`, error);
    }
    
    return nodes;
  };

  // Pre-built parent lookup map -- O(n) once instead of O(n*m) during render
  const parentMap = useMemo(() => {
    if (!treeData) return {};
    const map = {};
    const traverse = (node, parent) => {
      if (!node) return;
      if (node.username && parent) {
        map[node.username] = parent;
      }
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(child => traverse(child, node));
      }
    };
    traverse(treeData, null);
    return map;
  }, [treeData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchData(true); // Force refresh
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) {
    return <div className="p-4">{t('common.loading', 'Loading...')}</div>;
  }



  return (
    <div className="relative">
      {/* Floating refresh button */}
      <div className="fixed top-18 right-8 z-40">
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="inline-flex items-center gap-2 px-3 py-2 md:px-4 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          title={t('agentTree.refreshTree', 'Refresh tree data')}
        >
          <ArrowPathIcon 
            className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${isRefreshing ? 'animate-spin' : ''}`}
          />
          <span className="font-medium text-sm md:text-base text-gray-700 dark:text-gray-300">
            {t('common.refresh', 'Refresh')}
          </span>
        </button>
      </div>

      <div className="agent-tree-container">
        <div className="flex items-center justify-between w-full">
          <h1 className="agent-tree-title">
            {selectedType === USER_TYPES.PLAYER ? t('navigation.playerList', 'Player List') : t('navigation.agentTree', 'Agent Tree')}
          </h1>
        </div>
        <Legend onTypeSelect={handleTypeFilter} selectedType={selectedType} userType={userType} />
        <SearchBar 
          onSearch={setSearchTerm}
          searchTerm={searchTerm}
          onClear={() => setSearchTerm('')}
        />
        <div className="agent-tree-content">
          {debouncedSearchTerm && debouncedSearchTerm.length >= 3 ? (
            searchResults.length > 0 ? (
              <ul className="space-y-2">
                {searchResults.map(user => {
                  if (!user || typeof user !== 'object') {
                    console.warn("Invalid user found in search results:", user);
                    return null;
                  }
                  
                  const parentNode = parentMap[user.username] || null;
                  
                  return (
                    <TreeNodeErrorBoundary key={user.username || `search-${Math.random().toString(36).substring(7)}`}>
                      <TreeNode
                        node={user}
                        searchTerm={searchTerm}
                        isSearchResult={true}
                        path={`/${user.username || 'unknown'}`}
                        selectedType={selectedType}
                        rootUserType={userType}
                        parentNode={parentNode}
                        onOpenDeepView={handleOpenDeepView}
                      />
                    </TreeNodeErrorBoundary>
                  );
                })}
              </ul>
            ) : (
              <p className="text-gray-500 dark:text-gray-300">{t('users.noUsers', 'No users found.')}</p>
            )
          ) : selectedType ? (
            <ul className="space-y-2">
              {treeData && [...findNodesOfType(treeData, selectedType)].reverse().map(node => {
                if (!node || typeof node !== 'object') {
                  console.warn("Invalid node found in filtered results:", node);
                  return null;
                }
                
                const parentNode = parentMap[node.username] || null;
                
                return (
                  <TreeNodeErrorBoundary key={node.username || `node-${Math.random().toString(36).substring(7)}`}>
                    <TreeNode
                      node={node}
                      searchTerm=""
                      isSearchResult={false}
                      path={`/${node.username || 'unknown'}`}
                      selectedType={selectedType}
                      rootUserType={userType}
                      parentNode={parentNode}
                      onOpenDeepView={handleOpenDeepView}
                    />
                  </TreeNodeErrorBoundary>
                );
              })}
            </ul>
          ) : (
            treeData && (
              <TreeNode 
                node={treeData}
                searchTerm=""
                isSearchResult={false}
                path="/admin"
                selectedType={selectedType}
                rootUserType={userType}
                onOpenDeepView={handleOpenDeepView}
              />
            )
          )}
        </div>
      </div>

      {/* Deep View Modal - rendered at AgentTree level so it survives TreeNode unmounts */}
      {deepViewModalOpen && deepViewNode && (
        <UserDeepViewModal
          isOpen={deepViewModalOpen}
          onClose={handleCloseDeepView}
          node={deepViewNode}
          userType={userType}
          parentNode={deepViewParent}
          onNavigateToUser={handleNavigateToUser}
        />
      )}
    </div>
  );
};

export default AgentTree; 